/**
 * Recipes Hooks
 *
 * React Query hooks for Recipes data fetching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { formatCentimes } from '@/lib/format';
import {
  recipesService,
  posProductsWithoutRecipeService,
  type QueryRecipesDto,
  type CreateRecipeInput,
  type UpdateRecipeInput,
  type DuplicateRecipeInput,
  type AddIngredientInput,
  type UpdateIngredientInput,
  type PublishRecipeInput,
} from '../services/recipes.service';

/**
 * Hook to fetch paginated recipes list
 */
export function useRecipes(params: QueryRecipesDto) {
  return useQuery({
    queryKey: ['recipes', params],
    queryFn: () => recipesService.list(params),
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch recipe by ID
 */
export function useRecipe(id: string | null) {
  return useQuery({
    queryKey: ['recipes', id],
    queryFn: () => recipesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch recipe versions
 */
export function useRecipeVersions(id: string | null) {
  return useQuery({
    queryKey: ['recipes', id, 'versions'],
    queryFn: () => recipesService.getVersions(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch the flat view of all recipes (one row per ingredient)
 */
export function useRecipesFlatView(enabled = true) {
  return useQuery({
    queryKey: ['recipes', 'flat-view'],
    queryFn: () => recipesService.getFlatView(),
    // Donnees stables : on evite de recharger 1400 lignes a chaque aller-retour
    // entre les deux onglets.
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Hook to fetch recipe statistics
 */
export function useRecipeStats() {
  return useQuery({
    queryKey: ['recipes', 'stats'],
    queryFn: () => recipesService.getStats(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch POS products without recipes
 */
export function usePosProductsWithoutRecipe() {
  return useQuery({
    queryKey: ['pos-products', 'without-recipe'],
    queryFn: () => posProductsWithoutRecipeService.list(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch the current active recipe for a POS product
 */
export function useRecipeByPosProduct(posProductId: string | undefined) {
  return useQuery({
    queryKey: ['recipes', 'by-pos-product', posProductId],
    queryFn: async () => {
      const result = await recipesService.list({
        posProductId,
        limit: 1,
      });
      // Return the first (and should be only) current recipe
      return result.data[0] ?? null;
    },
    enabled: !!posProductId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch orphan recipes (recipes without a POS product)
 */
export function useOrphanRecipes(search?: string) {
  return useQuery({
    queryKey: ['recipes', 'orphan', search],
    queryFn: () => recipesService.list({
      hasProduct: false,
      search: search || undefined,
      limit: 50,
    }),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to create a recipe
 */
export function useCreateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRecipeInput) => recipesService.create(data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['pos-products', 'without-recipe'] });
      toast.success(`Recette "${recipe.name}" creee avec succes`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la creation de la recette';
      toast.error(message);
    },
  });
}

/**
 * Hook to update a recipe (creates new version)
 */
export function useUpdateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRecipeInput }) =>
      recipesService.update(id, data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipes', recipe.id] });
      toast.success(`Recette mise a jour (V${recipe.version})`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour';
      toast.error(message);
    },
  });
}

/**
 * Hook to duplicate a recipe
 */
export function useDuplicateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: DuplicateRecipeInput }) =>
      recipesService.duplicate(id, data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast.success(`Recette "${recipe.name}" dupliquee`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la duplication';
      toast.error(message);
    },
  });
}

/**
 * Hook to archive a recipe
 */
export function useArchiveRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipesService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['pos-products', 'without-recipe'] });
      toast.success('Recette archivee avec succes');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? "Erreur lors de l'archivage";
      toast.error(message);
    },
  });
}

/**
 * Hook to calculate recipe cost
 */
export function useCalculateRecipeCost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipesService.calculateCost(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: ['recipes', id] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      if (result.isComplete) {
        toast.success(`Cout calcule: ${formatCentimes(result.totalCost)}`);
      } else {
        toast.success(`Cout partiel: ${formatCentimes(result.totalCost)} (${result.missingPmpCount} PMP manquants)`);
      }
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors du calcul du cout';
      toast.error(message);
    },
  });
}

/**
 * Hook to add ingredient
 */
export function useAddIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recipeId, data }: { recipeId: string; data: AddIngredientInput }) =>
      recipesService.addIngredient(recipeId, data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.setQueryData(['recipes', recipe.id], recipe);
      toast.success('Ingredient ajoute');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? "Erreur lors de l'ajout de l'ingredient";
      toast.error(message);
    },
  });
}

/**
 * Hook to update ingredient
 */
export function useUpdateIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recipeId, ingredientId, data }: { recipeId: string; ingredientId: string; data: UpdateIngredientInput }) =>
      recipesService.updateIngredient(recipeId, ingredientId, data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.setQueryData(['recipes', recipe.id], recipe);
      toast.success('Ingredient modifie');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? "Erreur lors de la modification de l'ingredient";
      toast.error(message);
    },
  });
}

/**
 * Hook to remove ingredient
 */
export function useRemoveIngredient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recipeId, ingredientId }: { recipeId: string; ingredientId: string }) =>
      recipesService.removeIngredient(recipeId, ingredientId),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.setQueryData(['recipes', recipe.id], recipe);
      toast.success('Ingredient retire');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? "Erreur lors de la suppression de l'ingredient";
      toast.error(message);
    },
  });
}

/**
 * Hook to add a POS product to a recipe
 */
export function useAddRecipePosProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recipeId, posProductId }: { recipeId: string; posProductId: string }) =>
      recipesService.addPosProduct(recipeId, posProductId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['pos-products'] });
      toast.success('Produit POS associe');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? "Erreur lors de l'association";
      toast.error(message);
    },
  });
}

/**
 * Hook to remove a POS product from a recipe
 */
export function useRemoveRecipePosProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recipeId, posProductId }: { recipeId: string; posProductId: string }) =>
      recipesService.removePosProduct(recipeId, posProductId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['pos-products'] });
      toast.success('Produit POS dissocie');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la dissociation';
      toast.error(message);
    },
  });
}

// ========================================
// SPRINT 6 - DRAFT HOOKS
// ========================================

/**
 * Hook to create a recipe (always creates as draft)
 * Sprint 6: Renamed from useCreateDraft - create() always creates a draft now
 */
export function useCreateDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRecipeInput) => recipesService.create(data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast.success(`Brouillon "${recipe.name}" cree`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la creation du brouillon';
      toast.error(message);
    },
  });
}

/**
 * Hook to update a draft (direct modification)
 */
export function useUpdateDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRecipeInput }) =>
      recipesService.updateDraft(id, data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.setQueryData(['recipes', recipe.id], recipe);
      toast.success('Brouillon mis a jour');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour du brouillon';
      toast.error(message);
    },
  });
}

/**
 * Hook to publish a draft
 */
export function usePublishRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: PublishRecipeInput }) =>
      recipesService.publish(id, data),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.setQueryData(['recipes', recipe.id], recipe);
      toast.success(`Recette publiee (V${recipe.version})`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la publication';
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a draft
 */
export function useDeleteDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipesService.deleteDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast.success('Brouillon supprime');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la suppression du brouillon';
      toast.error(message);
    },
  });
}

/**
 * Hook to create a draft from an active recipe
 */
export function useCreateDraftFromActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recipesService.createDraftFromActive(id),
    onSuccess: (recipe) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast.success(`Brouillon cree pour "${recipe.name}"`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la creation du brouillon';
      toast.error(message);
    },
  });
}

/**
 * Hook to recalculate cost for all active recipes
 */
export function useRecalculateAllCosts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => recipesService.recalculateAllCosts(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      if (result.errorCount === 0) {
        toast.success(`${result.updated} recettes mises a jour`);
      } else {
        toast.success(`${result.updated} recettes mises a jour (${result.errorCount} erreurs)`);
      }
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors du recalcul des couts';
      toast.error(message);
    },
  });
}
